import { isBinaryContentType } from '../../custom-ui/bridge/methods/useProductFetchClient';
import type { MakeBridgeContext } from '../types';
import type { TBridgeMethod } from './types';

export type FetchRemotePayload = {
	fetchRequestInit: Omit<RequestInit, 'body'> & { body?: string | null; path?: string };
	isMultipartFormData: boolean;
	remoteKey: string;
};

export type FetchRemoteResponse = {
	body?: string;
	headers: Record<string, string>;
	isAttachment?: boolean;
	status: number;
	statusText: string;
};

type FetchRemoteContext = MakeBridgeContext<never, 'getContextToken' | 'remoteFetchClient'>;

/**
 * Makes an authenticated REST call to a Forge Remote backend on behalf of the
 * Forge app running inside the iframe.
 *
 * Happy path: acquires a context token, passes it to `remoteFetchClient` which
 * exchanges it for a FIT token (cached per-remoteKey for 30 minutes), then
 * calls the remote endpoint. The raw `Response` is serialised into a plain
 * object so it can be sent across the postMessage boundary.
 *
 * Binary content types (images, PDFs, etc.) are flagged via `isAttachment: true`;
 * `remoteFetchClient` base64-encodes binary responses before they reach here.
 *
 * @param data.remoteKey           - The remote key declared in the app manifest.
 * @param data.fetchRequestInit    - RequestInit options; the optional `path` field
 *                                   is extracted and forwarded as the REST sub-path
 *                                   on the remote base URL.
 * @param data.isMultipartFormData - When true, the client handles multipart encoding.
 */
export const fetchRemote: TBridgeMethod<
	FetchRemoteContext,
	FetchRemotePayload,
	FetchRemoteResponse
> = async (
	{ services: { getContextToken, remoteFetchClient } },
	{ remoteKey, fetchRequestInit, isMultipartFormData },
) => {
		const { path, ...init } = fetchRequestInit;
		const contextToken = await getContextToken();

		const response = await remoteFetchClient(remoteKey, init, isMultipartFormData, path, contextToken);

		const { status, statusText } = response;
		const headers = Object.fromEntries(response.headers.entries());
		const isAttachment = isBinaryContentType(response.headers.get('content-type'));
		const body = response.body ? await response.text() : undefined;

		return { headers, status, statusText, body, isAttachment };
	};
